import { FeatureToggleState as _FeatureToggleState } from '@interfaces/state';
import { Action, createReducer, on } from '@ngrx/store';

import * as featureActions from '../../actions';

export { FeatureToggle } from '@interfaces';

export interface FeatureToggleState extends _FeatureToggleState {}

export interface IFeatureToggle {
  featureId: string;
  enabled: boolean;
}

export const initialState: FeatureToggleState = {
  benes_by_relation: true,
  save_as_draft: false,
  control_person: true,
  gifting: true,
  gifting_scale_v2: true,
  cwp_ira_scale_trad: false,
  cwp_ira_scale_inh_roth: false,
  cwp_ira_scale_estate: false,
  cwp_ira_scale_trust: false,
  cwp_inheritance_ira_question: false,
  cwp_ira_scale_trust_to_trust: false,
  cwp_internal_gifting_managed_accounts: false,
  intelledox_redirect_and_thresholds: false,
  joint_inheritance: false,
  inheritance_individual: false,
  inheritance_ira: false,
  doc_checklist_inheritance: false,
  cwp_multi_inheritance: false,
  cwp_multi_inheritance_estate: false,
  cwp_inheritance_save_draft: false,
  cwp_inheritance_save_draft_internal: false,
  cwp_co0_minor: false,
  cwp_co0_inheritance: false,
  cwp_co0_internal_transfers: false,
  cwp_co0_upload: false,
  cwp_co0_gifting: false,
  cwp_divorce: false,
  cwp_gifting_advised_accounts: false,
  cwp_it_multi_receiving: false,
  cwp_multi_transactions_crew: false,
  cwp_account_validation: false,
  cwp_inheritance_vcd: false,
  togglesLoaded: false,
  cwp_prospect_to_auth_first: false,
  l8_itw_upload: false,
  cwp_vc_org_to_org: false,
  cwp_non_vc_org_transfer: false,
  cwp_document_upload_mfe: false,
  cwp_inheritance_org_as_bene: false,
  cwp_mo_gifting: false,
  cwp_inheritance_individual_joint_to_trust: false,
  cwp_inheritance_restrict_dup_transactions: false,
  cwp_inheritance_decedent_security: false,
  cwp_inheritance_checklist_get_started: false,
  cwp_terms_w9_redesign: false,
  cwp_multi_it_mo: false,
  cwp_inheritance_advised_client_warnings: false,
  cwp_inheritance_save_state: false,
  cwp_inheritance_new_terms: false,
  cwp_minor_save_draft: false,
  cwp_cash_plus_sweep_transfer: false,
  cwp_buy_sell_home_url_routing: false,
  cwp_viadoc_shadow_mode: false,
};

const featureToggleReducer = createReducer(
  initialState,
  on(featureActions.EnableFeatureToggles, (state, { payload }) => ({
    ...state,
    ...payload.reduce((obj, feature) => ({ ...obj, [feature]: true }), {}),
  })),
  on(featureActions.UpdateFeatureToggleValue, (state, { payload }) => ({
    ...state,
    [payload.key]: payload.value,
  })),
  on(featureActions.FeatureTogglesLoaded, (state) => ({
    ...state,
    togglesLoaded: true,
  })),
);

export function reducer(state: FeatureToggleState | undefined, action: Action) {
  return featureToggleReducer(state, action);
}

export const getGiftingScaleToggleV2Value = (state: FeatureToggleState) => state.gifting_scale_v2;

export const getIRAInheritedRothToggle = (state: FeatureToggleState) =>
  state.cwp_ira_scale_inh_roth;

export const getIRAInheritedTraditionalToggle = (state: FeatureToggleState) =>
  state.cwp_ira_scale_trad;

export const getIRATrustToggle = (state: FeatureToggleState) => state.cwp_ira_scale_trust;

export const getMultiInheritanceToggle = (state: FeatureToggleState) => state.cwp_multi_inheritance;

export const getMultiInheritanceEstateToggle = (state: FeatureToggleState) =>
  state.cwp_multi_inheritance_estate;

export const getInheritanceSaveDraftToggle = (state: FeatureToggleState) =>
  state.cwp_inheritance_save_draft;

export const getInheritanceSaveDraftInternalToggle = (state: FeatureToggleState) =>
  state.cwp_inheritance_save_draft_internal;

export const getDivorceToggle = (state: FeatureToggleState) => state.cwp_divorce;

export const getMultiTransactionCrewToggle = (state: FeatureToggleState) =>
  state.cwp_multi_transactions_crew;

export const getProspectToAuthFirstToggle = (state: FeatureToggleState) =>
  state.cwp_prospect_to_auth_first;

export const getL8ItwUploadToggle = (state: FeatureToggleState) => state.l8_itw_upload;

export const getVCOrgToOrgToggle = (state: FeatureToggleState) => state.cwp_vc_org_to_org;

export const getNonVCOrgTransferToggle = (state: FeatureToggleState) =>
  state.cwp_non_vc_org_transfer;

export const getDocumentUploadMFEToggle = (state: FeatureToggleState) =>
  state.cwp_document_upload_mfe;

export const getOrgAsBeneToggle = (state: FeatureToggleState) => state.cwp_inheritance_org_as_bene;

export const getMOGiftingToggle = (state: FeatureToggleState) => state.cwp_mo_gifting;

export const getInheritanceIndividualJointToTrustToggle = (state: FeatureToggleState) =>
  state.cwp_inheritance_individual_joint_to_trust;

export const getInheritanceDecedentSecurityToggle = (state: FeatureToggleState) =>
  state.cwp_inheritance_decedent_security;

export const getChecklistGetStartedToggle = (state: FeatureToggleState) =>
  state.cwp_inheritance_checklist_get_started;

export const getCwpTermsW9RedesignToggle = (state: FeatureToggleState) =>
  state.cwp_terms_w9_redesign;

export const getCwpMultiItMoToggle = (state: FeatureToggleState) => state.cwp_multi_it_mo;

export const getCwpMinorSaveDraftToggle = (state: FeatureToggleState) => state.cwp_minor_save_draft;

export const getCwpCashPlusSweepTransferToggle = (state: FeatureToggleState) =>
  state.cwp_cash_plus_sweep_transfer;

export const getCwpBuySellHomeUrlRoutingToggle = (state: FeatureToggleState) =>
  state.cwp_buy_sell_home_url_routing;

export const getViadocShadowModeToggle = (state: FeatureToggleState) =>
  state.cwp_viadoc_shadow_mode;
